import React, {useEffect, useMemo, useRef, useState} from "react";
import {useSite} from "../../context/SiteContext";
import {COUNTRY_BY_ID, COUNTRY_COLOR, EXTRA_COUNTRY_COLORS, SPOT_DUMMY_VIDEO} from "../../data/siteData";
import {getWebsiteLocations} from "../../services/CommonService";
import {GRAD, mediaUrl, prefersReducedMotion, stagger} from "../../utils";
import useScrollReveal from "../../hooks/useScrollReveal";
import Icon from "../common/Icon";
import Bars from "../common/Bars";
import ImageSlot from "../common/ImageSlot";
import LocationMap from "./LocationMap";

// Muted loop for a location card; only plays while the card is on screen. With reduced motion
// it stays paused on its first frame.
const SpotVideo = ({src}) => {
    const ref = useRef(null);
    const reduced = prefersReducedMotion();

    useEffect(() => {
        const video = ref.current;
        if (!video || reduced || !('IntersectionObserver' in window)) return;
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
            if (entry.isIntersecting) {
                const playing = video.play();
                if (playing && playing.catch) playing.catch(() => {
                });
            } else {
                video.pause();
            }
        }), {threshold: 0.35});
        observer.observe(video);
        return () => observer.disconnect();
    }, [reduced]);

    if (!src) return null;
    if (reduced) {
        // "#t=0.1" makes browsers draw a frame instead of a blank box.
        return <video src={mediaUrl(src) + '#t=0.1'} muted playsInline preload="metadata" aria-hidden="true"
                      className="pb-spot-video" style={{opacity: 1}}/>;
    }
    return (
        <video ref={ref} src={mediaUrl(src)} muted loop playsInline preload="none" aria-hidden="true"
               className="pb-spot-video" onPlaying={(e) => {
            e.currentTarget.style.opacity = 1;
        }}/>
    );
};

// The API stores names in capitals ("ÅLSHULT"); show those as "Ålshult".
const displayName = (name) => name === name.toUpperCase()
    ? name.charAt(0) + name.slice(1).toLowerCase()
    : name;

// "2026-11-01" -> "1 Nov 2026" ("1 Nov" without the year), read as a calendar date so the time zone cannot shift it.
const formatDate = (value, withYear = true) => {
    const [y, m, d] = String(value).split('-').map(Number);
    if (!y || !m || !d) return '';
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', ...(withYear && {year: 'numeric'})});
};

// What the card shows for each booking_status from the API. Customers can book an upcoming
// season in advance, so only closed boxes and seasons that are over cannot be booked.
// `tone` styles the badge and the season line: open, upcoming or unavailable.
const bookingInfo = (loc) => {
    const start = formatDate(loc.season_start_date);
    const end = formatDate(loc.season_end_date);
    const season = start && end ? `Season ${start} – ${end}` : '';
    switch (loc.booking_status) {
        case 'closed':
            return {
                bookable: false, tone: 'unavailable', label: 'Closed',
                note: loc.reason ? `Closed: ${loc.reason}` : 'Closed for booking right now'
            };
        case 'upcoming':
            return {
                bookable: true, tone: 'upcoming',
                label: start ? `Opens ${formatDate(loc.season_start_date, false)}` : 'Opening soon',
                note: start && end ? `Book now for the season ${start} – ${end}` : 'Booking open for the coming season'
            };
        case 'ended':
            return {bookable: false, tone: 'unavailable', label: 'Season over', note: season};
        default:
            return {bookable: true, tone: 'open', label: 'Open for booking', note: season};
    }
};

// Turn an API location into the shape the cards and the map use.
const toSpot = (loc) => {
    const parts = String(loc.address || '').split(',').map(p => p.trim()).filter(Boolean);
    // The address ends with the country name, which the card shows on its own line.
    const country = loc.country_name || COUNTRY_BY_ID[loc.country_id] || parts[parts.length - 1] || '';
    if (parts.length > 1 && parts[parts.length - 1] === country) parts.pop();
    const address = parts.join(', ');
    const name = displayName(String(loc.name || ''));
    return {
        id: loc.id, name, country, address,
        lat: Number(loc.latitude), lng: Number(loc.longitude),
        // Uploaded video (with the uploaded image as its poster), else the uploaded image, else the default video.
        image: loc.image || '',
        video: loc.video || (loc.image ? '' : SPOT_DUMMY_VIDEO),
        booking: bookingInfo(loc)
    };
};

// Countries in the order their first box appears, each with its colour.
const countriesOf = (spots) => {
    let extra = 0;
    return [...new Set(spots.map(s => s.country).filter(Boolean))].map(name => ({
        name,
        color: COUNTRY_COLOR[name] || EXTRA_COUNTRY_COLORS[extra++ % EXTRA_COUNTRY_COLORS.length]
    }));
};

// "Sweden", "Sweden and Denmark", "Sweden, Denmark and Norway", each name in its country colour.
const CountryList = ({countries}) => countries.map((c, i) => (
    <React.Fragment key={c.name}>
        {i > 0 && (i === countries.length - 1 ? ' and ' : ', ')}
        <span style={{color: c.color}}>{c.name}</span>
    </React.Fragment>
));

const LocationsSection = () => {
    const {openApp} = useSite();
    const [country, setCountry] = useState('All');
    const [allSpots, setAllSpots] = useState([]);
    const [status, setStatus] = useState('loading');

    useEffect(() => {
        let active = true;
        getWebsiteLocations()
            .then(res => {
                if (!active) return;
                const list = (res.data && res.data.data) || [];
                setAllSpots(list.map(toSpot).filter(s => !isNaN(s.lat) && !isNaN(s.lng)));
                setStatus('ready');
            })
            .catch(() => active && setStatus('error'));
        return () => {
            active = false;
        };
    }, []);

    const countries = useMemo(() => countriesOf(allSpots), [allSpots]);
    const colorOf = (name) => (countries.find(c => c.name === name) || {}).color || '#0B72C9';
    const spots = useMemo(() => allSpots.filter(s => country === 'All' || s.country === country), [allSpots, country]);

    // Filtering or loading swaps the cards, so pick up the new ones for the reveal effect.
    useScrollReveal([country, allSpots]);

    return (
        <>
            <section id="loc" className="pb-container pb-intro">
                <p className="pb-pill" style={{background: 'rgba(244,123,32,0.14)', color: '#d2660f', marginBottom: 12}}>
                    <Icon name="pin" size={16}/>Where to paddle
                </p>
                <h2 className="pb-intro-title" style={{maxWidth: '20ch'}}>
                    Boxes on the water{countries.length > 0 && <> in <CountryList countries={countries}/></>}
                </h2>
                <div className="pb-rule"><Bars w={16} h={4} gap={4}/></div>
                <p className="pb-lead">Every box we run, with its exact address. Live availability and booking are in
                    the app — tap any box below to get it.</p>
            </section>
            <section className="pb-container pb-section-body">
                {/* Filtering only makes sense with more than one country. */}
                {countries.length > 1 && <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
                    {['All', ...countries.map(c => c.name)].map(label => {
                        const active = country === label;
                        return (
                            <button key={label} onClick={() => setCountry(label)} className="pb-country-chip" style={{
                                background: active ? 'linear-gradient(135deg,#2E9BE0,#0B72C9)' : '#fff',
                                color: active ? '#fff' : '#3c4d68',
                                boxShadow: active ? '0 16px 30px -20px rgba(11,114,201,0.95)' : '0 14px 30px -26px rgba(12,38,84,0.7)'
                            }}>{label}</button>
                        );
                    })}
                </div>}
                <LocationMap spots={spots} country={country}/>
                {status === 'loading' && <p className="pb-lead">Loading boxes…</p>}
                {status === 'error' && <p className="pb-lead">We couldn't load the boxes right now. Please try again later.</p>}
                <div className="pb-spots">
                    {spots.map((s, i) => {
                        const color = colorOf(s.country);
                        const mapHref = 'https://www.google.com/maps/search/?api=1&query=' +
                            encodeURIComponent(s.address + ', ' + s.country);
                        return (
                            <div key={s.id} className="pb-reveal pb-spot hover-card-lift"
                                 style={{animationDelay: stagger(i)}}>
                                <div className="pb-spot-media">
                                    <ImageSlot src={s.image} alt={s.image ? s.name : ''}/>
                                    <SpotVideo src={s.video}/>
                                    <span className="pb-spot-badge" style={{background: color}}>
                                        <Icon name="pin" size={16}/>{s.country}
                                    </span>
                                    <span className={'pb-spot-status is-' + s.booking.tone}>
                                        {s.booking.label}
                                    </span>
                                </div>
                                <div className="pb-spot-body">
                                    <h3 className="pb-spot-name">{s.name}</h3>
                                    <p className="pb-spot-address">{s.address}<br/>{s.country}</p>
                                    {s.booking.note && (
                                        <p className={'pb-spot-season is-' + s.booking.tone}>
                                            <Icon name={s.booking.bookable ? 'cal' : 'clock'} size={15}/>{s.booking.note}
                                        </p>
                                    )}
                                    <div className="pb-spot-actions">
                                        <a href={mapHref} target="_blank" rel="noopener noreferrer" className="pb-spot-link">
                                            <Icon name="arrow" size={17}/>Directions
                                        </a>
                                        {s.booking.bookable ? (
                                            <button onClick={openApp} className="pb-spot-book hover-brighten"
                                                    style={{background: GRAD[color]}}>Book in app
                                            </button>
                                        ) : (
                                            <button className="pb-spot-book" disabled>Not bookable</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </>
    );
};

export default LocationsSection;
